import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { plants } from "@/lib/api";
import { MAX_EXTRA_PHOTOS_FE } from "@/lib/galleryUtils";

/**
 * Owns the entire Gallery bulk-merge flow:
 *   - "Do we need the photo picker?" fast path (`handleMergeIntent`)
 *   - Picker state (candidates, selection, primary metadata)
 *   - The `bulkMerge` call to the API + optimistic list update + Undo toast
 *
 * Behavior-preserving extraction from Gallery.jsx (iter56).
 *
 * @param {object} args
 * @param {Array<any>|null} args.items
 * @param {Set<string>} args.selected
 * @param {React.Dispatch<React.SetStateAction<Array<any>|null>>} args.setItems
 * @param {React.Dispatch<React.SetStateAction<Set<string>>>} args.setSelected
 * @param {(v: boolean) => void} args.setSelectMode
 * @param {(v: boolean) => void} args.setPendingBulkMerge
 */
export default function useGalleryMerge({
  items,
  selected,
  setItems,
  setSelected,
  setSelectMode,
  setPendingBulkMerge,
}) {
  const { t } = useTranslation();
  const [merging, setMerging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerCandidates, setPickerCandidates] = useState([]); // [{key, scan_id, photo_id, thumb, sourceLabel}]
  const [pickerSelection, setPickerSelection] = useState([]);   // ordered keys, ≤ MAX_EXTRA_PHOTOS_FE
  const [pickerPrimary, setPickerPrimary] = useState(null);     // {scan_id, thumb, primaryExtrasCount}

  const handleMergeIntent = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length < 2) return;
    // Chronological — oldest becomes primary.
    const chronological = [...ids].sort((a, b) => {
      const ta = (items?.find((x) => x.scan_id === a)?.created_at) || "";
      const tb = (items?.find((x) => x.scan_id === b)?.created_at) || "";
      return ta.localeCompare(tb);
    });
    const primaryId = chronological[0];
    const otherIds = chronological.slice(1);
    // Fast path — sum candidate photos from list metadata. Each merged scan
    // contributes 1 (its primary) + extra_images_count. If that fits under
    // the remaining primary-side capacity, no picker is needed.
    const candidatesTotal = otherIds.reduce((sum, sid) => {
      const it = items.find((x) => x.scan_id === sid);
      return sum + 1 + (it?.extra_images_count || 0);
    }, 0);
    const primaryItem = items.find((x) => x.scan_id === primaryId);
    const primaryExtras = primaryItem?.extra_images_count || 0;
    const remainingSlots = MAX_EXTRA_PHOTOS_FE - primaryExtras;
    if (remainingSlots <= 0 || candidatesTotal <= remainingSlots) {
      setPendingBulkMerge(true);
      return;
    }
    // Picker needed — fetch details for each merged scan so we can render
    // thumbnails. Fetch primary too so we can show its photo(s) locked.
    setPickerOpen(true);
    setPickerLoading(true);
    setPickerSelection([]);
    setPickerCandidates([]);
    setPickerPrimary(null);
    try {
      const details = await Promise.all([primaryId, ...otherIds].map((id) => plants.get(id)));
      const [primary, ...others] = details.map((d) => d.data);
      // Build the candidate list — 1 primary + N extras per scan.
      const cands = [];
      for (const o of others) {
        cands.push({
          key: `${o.scan_id}|primary`,
          scan_id: o.scan_id,
          photo_id: null,
          thumb: o.thumb_base64 || o.image_base64,
          sourceLabel: o?.identification?.common_name || t("gallery.card.unknown"),
        });
        for (const ex of (o.extra_images || [])) {
          cands.push({
            key: `${o.scan_id}|${ex.photo_id}`,
            scan_id: o.scan_id,
            photo_id: ex.photo_id,
            thumb: ex.image_base64,
            sourceLabel: o?.identification?.common_name || t("gallery.card.unknown"),
          });
        }
      }
      setPickerCandidates(cands);
      setPickerPrimary({
        scan_id: primary.scan_id,
        thumb: primary.thumb_base64 || primary.image_base64,
        name: primary?.identification?.common_name || t("gallery.card.unknown"),
        primaryExtrasCount: (primary.extra_images || []).length,
      });
      // Pre-select the first `remainingSlots` candidates so the button is
      // enabled by default; user can uncheck to substitute.
      setPickerSelection(cands.slice(0, remainingSlots).map((c) => c.key));
    } catch {
      toast.error(t("gallery.toastMergeFail"));
      setPickerOpen(false);
    } finally {
      setPickerLoading(false);
    }
  }, [items, selected, setPendingBulkMerge, t]);

  const togglePickerCandidate = useCallback((key) => {
    setPickerSelection((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= MAX_EXTRA_PHOTOS_FE - (pickerPrimary?.primaryExtrasCount || 0)) return prev;
      return [...prev, key];
    });
  }, [pickerPrimary]);

  const bulkMerge = useCallback(async (selectedPhotos = null) => {
    const ids = Array.from(selected);
    if (ids.length < 2) { setPendingBulkMerge(false); return; }
    const chronological = [...ids].sort((a, b) => {
      const ta = (items?.find((x) => x.scan_id === a)?.created_at) || "";
      const tb = (items?.find((x) => x.scan_id === b)?.created_at) || "";
      return ta.localeCompare(tb);
    });
    const primary = chronological[0];
    const others = chronological.slice(1);
    const preMergeItems = items;
    setMerging(true);
    try {
      const { data } = await plants.merge(primary, others, selectedPhotos);
      setItems((prev) => (prev || [])
        .filter((i) => !others.includes(i.scan_id))
        .map((i) => i.scan_id === primary
          ? { ...i, extra_images_count: data?.extra_images_count ?? ((i.extra_images_count || 0) + others.length) }
          : i));
      const dropped = data?.extra_images_dropped || 0;
      const successMsg = dropped > 0
        ? t("gallery.toastMergedWithDropped", { n: data.merged_count, dropped })
        : t("gallery.toastMerged", { n: data.merged_count });
      if (data?.snapshot_id) {
        toast.success(successMsg, {
          duration: 10_000,
          action: {
            label: t("common.undo"),
            onClick: async () => {
              try {
                await plants.undo(data.snapshot_id);
                setItems(preMergeItems);
                toast.success(t("gallery.toastMergeUndone"));
              } catch (e) {
                const detail = e?.response?.data?.detail;
                toast.error(detail === "undo_expired" ? t("gallery.toastUndoExpired") : t("gallery.toastUndoFail"));
              }
            },
          },
        });
      } else {
        toast.success(successMsg);
      }
      setSelected(new Set());
      setSelectMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gallery.toastMergeFail"));
    } finally {
      setMerging(false);
      setPendingBulkMerge(false);
      setPickerOpen(false);
    }
  }, [items, selected, setItems, setSelected, setSelectMode, setPendingBulkMerge, t]);

  return {
    // state
    merging,
    pickerOpen,
    pickerLoading,
    pickerCandidates,
    pickerSelection,
    pickerPrimary,
    // setters exposed for close-picker handler in JSX
    setPickerOpen,
    // actions
    handleMergeIntent,
    togglePickerCandidate,
    bulkMerge,
  };
}
